import { useState } from 'react';
import { Switch } from '@headlessui/react';
import { cn, formatCurrency } from '@/utils';
import type { AddonOption } from '@/types/integrations';

interface AddonSelectorProps {
  addons: AddonOption[];
  onChange: (selected: string[]) => void;
}

function formatINR(amount: number) {
  return formatCurrency(amount);
}

export function AddonSelector({ addons, onChange }: AddonSelectorProps) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(addons.filter((a) => a.selected).map((a) => a.code))
  );

  function toggle(code: string) {
    const next = new Set(selected);
    if (next.has(code)) {
      next.delete(code);
    } else {
      next.add(code);
    }
    setSelected(next);
    onChange(Array.from(next));
  }

  const totalCost = addons
    .filter((a) => selected.has(a.code) && a.available)
    .reduce((sum, a) => sum + a.premium, 0);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Add-ons</span>
        {totalCost > 0 && (
          <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
            +{formatINR(totalCost)}
          </span>
        )}
      </div>
      <ul className="divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {addons.map((addon) => (
          <li
            key={addon.code}
            className={cn(
              'flex items-center justify-between px-4 py-3',
              !addon.available && 'opacity-50 bg-gray-50 dark:bg-gray-800/50'
            )}
          >
            <div className="flex items-center gap-3 min-w-0">
              <Switch
                checked={selected.has(addon.code) && addon.available}
                onChange={() => addon.available && toggle(addon.code)}
                disabled={!addon.available}
                className={cn(
                  'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                  selected.has(addon.code) && addon.available
                    ? 'bg-primary-600'
                    : 'bg-gray-200 dark:bg-gray-600',
                  !addon.available && 'cursor-not-allowed'
                )}
              >
                <span
                  className={cn(
                    'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    selected.has(addon.code) && addon.available ? 'translate-x-4' : 'translate-x-0'
                  )}
                />
              </Switch>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{addon.name}</p>
                {!addon.available && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">Not available</p>
                )}
              </div>
            </div>
            <span className="ml-3 flex-shrink-0 text-sm font-medium text-gray-700 dark:text-gray-300">
              {formatINR(addon.premium)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
